import React, { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { Select, Space, InputNumber } from 'antd';
import '../../css/Decomposition.css';

const { Option } = Select;

// Interface pour les données de décomposition observée
interface DecompositionResidualUC2Data {
    id: string;
    TimeWindow: Date;
    TotalTraffic: number;
}

// Génération de données statiques pour le graphique
const generateMockData = (): DecompositionResidualUC2Data[] => {
    const data: DecompositionResidualUC2Data[] = [];
    const baseTime = new Date();
    baseTime.setHours(9, 0, 0, 0); // Commencer à 9h00
    
    // Générer des données pour une journée (de 9h à 20h)
    for (let i = 0; i < 500; i++) {
        const time = new Date(baseTime.getTime() + i * 60000); // Ajouter i minutes
        
        // Créer des valeurs de trafic qui ressemblent à celles d'un graphique de décomposition
        // Formule: tendance + cycle + bruit aléatoire
        let trafficValue = 0;
        
        // Tendance de base (croissance légère)
        const trend = i * 5;
        
        // Composante cyclique (pattern journalier)
        const cycle = 5000 * Math.sin(i / 50) + 3000 * Math.sin(i / 20);
        
        // Bruit aléatoire
        const noise = (Math.random() - 0.5) * 2000;
        
        trafficValue = trend + cycle + noise;
        // Assurer que les valeurs sont positives pour la plupart
        trafficValue = Math.max(trafficValue, -1000);
        
        data.push({
            id: `data-${i}`,
            TimeWindow: time,
            TotalTraffic: trafficValue
        });
    }
    
    return data;
};

const DecompositionResidualUC2Graph: React.FC = () => {
    const [data, setData] = useState<DecompositionResidualUC2Data[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [filterType, setFilterType] = useState<string>('none');
    const [filterValue, setFilterValue] = useState<number>(1000);

    useEffect(() => {
        // Simuler un chargement
        setTimeout(() => {
            try {
                const mockData = generateMockData();
                setData(mockData);
                setLoading(false);
            } catch (err) {
                console.error('Error generating mock data:', err);
                setError('Failed to generate data');
                setLoading(false);
            }
        }, 500);
    }, []);

    const getFilteredData = () => {
        if (filterType === 'none') return data;
        
        return data.filter(item => {
            const trafficValue = Math.abs(item.TotalTraffic);
            switch (filterType) {
                case 'above':
                    return trafficValue >= filterValue;
                case 'below':
                    return trafficValue <= filterValue;
                default:
                    return true;
            }
        });
    };

    const processData = () => {
        if (!data || data.length === 0) {
            return {
                xAxis: { type: 'category', data: [] },
                yAxis: { type: 'value' },
                series: [{ data: [], type: 'line' }]
            };
        }

        const filteredData = getFilteredData();
        const timeWindow = filteredData.map(item => {
            const date = new Date(item.TimeWindow);
            return date.toLocaleTimeString('fr-FR', {
                hour: '2-digit',
                minute: '2-digit'
            });
        });

        const totalTraffic = filteredData.map(item => Number(item.TotalTraffic.toFixed(2)));

        return {
            // Pas de titre dans les options du graphique pour éviter la duplication
            tooltip: {
                trigger: 'axis',
                formatter: function(params: any) {
                    const value = params[0].value;
                    return `Time: ${params[0].name}<br/>Total Traffic: ${value.toLocaleString()} KBytes`;
                },
                axisPointer: {
                    type: 'cross',
                    label: {
                        backgroundColor: '#6a7985'
                    }
                }
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '15%',
                top: '15%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: timeWindow,
                boundaryGap: false,
                axisLabel: {
                    rotate: 45,
                    fontSize: 10,
                    interval: 'auto'
                },
                axisTick: {
                    alignWithLabel: true
                }
            },
            yAxis: {
                type: 'value',
                name: 'Total Traffic (KBytes)',
                nameLocation: 'middle',
                nameGap: 50,
                scale: true,
                splitLine: {
                    show: true,
                    lineStyle: {
                        type: 'dashed'
                    }
                }
            },
            dataZoom: [
                {
                    type: 'slider',
                    show: true,
                    xAxisIndex: [0],
                    start: 0,
                    end: 100,
                    height: 20,
                    bottom: 0,
                    borderColor: 'transparent',
                    backgroundColor: '#e2e2e2',
                    fillerColor: 'rgba(84, 112, 198, 0.2)',
                    handleStyle: {
                        color: '#5470c6'
                    }
                },
                {
                    type: 'inside',
                    xAxisIndex: [0]
                }
            ],
            series: [
                {
                    name: 'Total Traffic',
                    type: 'line',
                    smooth: true,
                    symbol: 'none',
                    sampling: 'average',
                    itemStyle: {
                        color: '#5470c6'
                    },
                    areaStyle: {
                        color: {
                            type: 'linear',
                            x: 0,
                            y: 0,
                            x2: 0,
                            y2: 1,
                            colorStops: [{
                                offset: 0,
                                color: 'rgba(84, 112, 198, 0.3)'
                            }, {
                                offset: 1,
                                color: 'rgba(84, 112, 198, 0)'
                            }]
                        }
                    },
                    data: totalTraffic,
                    markPoint: {
                        data: [
                            { type: 'max', name: 'Maximum' },
                            { type: 'min', name: 'Minimum' }
                        ]
                    }
                }
            ]
        };
    };

    return (
        <div className="decomposition-container">
            <h2 className="decomposition-title">Decomposition Residual UC2</h2>
            <Space className="filter-controls" style={{ margin: '10px 0' }}>
                <Select 
                    value={filterType}
                    onChange={setFilterType}
                    style={{ width: 120 }}
                >
                    <Option value="none">No Filter</Option>
                    <Option value="above">Above Value</Option>
                    <Option value="below">Below Value</Option>
                </Select>
                {filterType !== 'none' && (
                    <InputNumber
                        value={filterValue}
                        onChange={(value) => setFilterValue(value || 0)}
                        style={{ width: 120 }}
                        min={0}
                        step={1000}
                    />
                )}
            </Space>
            {loading ? (
                <div className="decomposition-loading">Loading data...</div>
            ) : error ? (
                <div className="decomposition-error">{error}</div>
            ) : (
                <div className="decomposition-graph">
                    <ReactECharts 
                        option={processData()} 
                        style={{ height: '100%', width: '100%' }}
                        opts={{ renderer: 'canvas' }}
                    />
                </div>
            )}
        </div>
    );
};

export default DecompositionResidualUC2Graph;
